import moment from 'moment';
import Alert from 'react-s-alert';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Spinner } from 'react-bootstrap';
import { Colors } from '../../../../themes/colors';
import { TabViewContainer } from '../../../commonComponents/TabViewContainer';
import { Table } from '../../../commonComponents/Table/Table';
import { style } from '../../../commonComponents/user-column';
import history from '../../../../history';
import { usePermanentJobList } from './hooks/usePermanentJobList';
import { Filters } from './components/Filters';
import { createLoadingSelector } from '../../../../apis/selectors';

const loadingSelector = createLoadingSelector(['GET_ALL_METADATA', 'GET_ALL_PROFESSIONS']);

export const PermanentJobListV2 = () => {
  const {
    data,
    isLoading,
    filters,
    total,
    totalPages,
    isError,
    error,
    handleFilterChanged,
    handlePageChanged,
    handleSortChanged,
  } = usePermanentJobList();

  const isInitialLoading = useSelector((state) => loadingSelector(state));

  useEffect(() => {
    if (isError) {
      Alert.error(error?.response?.data.error || error?.message, {
        position: 'bottom-right',
        effect: 'slide',
        beep: false,
        timeout: 5000,
        offset: 100,
      });
    }
  }, [isError, error]);

  if (isInitialLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', marginTop: '50px' }}>
        <Spinner
          animation="border"
          role="status"
          variant="info"
          style={{
            height: 30,
            width: 30,
            marginLeft: 20,
            marginTop: 10,
          }}
        />
      </div>
    );
  }

  return (
    <div
      style={{
        display: 'flex',
        height: '100vh',
        overflowY: 'auto',
        flexDirection: 'column',
        position: 'relative',
        alignItems: 'center',
      }}
    >
      <TabViewContainer
        title="Permanent Jobs"
        style={{
          marginInline: 'auto',
          width: '95%',
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          <div style={{ alignSelf: 'flex-end' }}>
            <button
              type="button"
              className="btn btn-info"
              onClick={() => history.push('/permanent-job/post')}
            >
              Post Permanent Job
            </button>
          </div>
          <Filters filters={filters} handleFilterChanged={handleFilterChanged} />
          <div style={{ alignSelf: 'flex-end' }}>
            <span
              style={{
                fontSize: 20,
                fontWeight: 'bold',
                color: Colors.neutral_600,
              }}
            >
              Total | {total !== undefined ? Intl.NumberFormat().format(total) : ''}
            </span>
          </div>
        </div>
        <TabViewContainer style={{ margin: 0, padding: 20 }}>
          <Table
            filter={filters}
            columns={[
              {
                id: 'status',
                selector: ({ status }) => status,
                name: 'Status',
                width: '150px',
                style: { ...style.tableText, textTransform: 'capitalize' },
              },
              {
                id: 'Listing Name',
                selector: ({ title }) => title,
                name: 'Listing Name',
                style: { ...style.tableText },
              },
              {
                id: 'officeName',
                selector: ({ officeName }) => officeName,
                format: (row) => (
                  <span
                    style={{
                      cursor: 'pointer',
                      textDecoration: 'underline',
                    }}
                    onClick={() => history.push(`/user/dentist/${row.officeId}`)}
                  >
                    {row?.officeName}
                  </span>
                ),
                name: 'Office Name',
                style: style.tableText,
              },
              {
                id: 'candidateCount',
                selector: ({ candidateCount }) => candidateCount || 0,
                name: 'Candidates',
                width: '120px',
                style: style.tableText,
              },
              {
                id: 'createdAt',
                selector: ({ createdAt }) =>
                  createdAt ? moment(createdAt).format('ddd, MMM Do, YYYY') : '-',
                name: 'Created',
                sortable: true,
                style: style.tableText,
              },
              {
                id: 'updatedAt',
                selector: ({ updatedAt }) =>
                  updatedAt ? moment(updatedAt).format('ddd, MMM Do, YYYY') : '-',
                name: 'Last Edited',
                sortable: true,
                style: style.tableText,
              },
              {
                id: 'planName',
                selector: ({ planName }) => planName || '--',
                name: 'Plan',
                width: '150px',
                style: style.tableText,
              },
              {
                id: 'recruiterEmail',
                selector: ({ recruiterEmail }) => recruiterEmail || '--',
                name: 'Recruiter',
                style: style.tableText,
              },
            ]}
            initialPage={filters.page}
            data={data}
            forceLoading={isLoading}
            noDataComponent="There are no Permanent Jobs"
            isLoading={isLoading}
            totalPages={totalPages}
            paginationPerPage={filters.limit}
            paginationTotalRows={total}
            onPageChange={handlePageChanged}
            onSort={handleSortChanged}
            onRowClicked={(row) => {
              if (row.status !== 'draft') {
                history.push(`/permanent-job/office/${row.officeId}/job/${row.id}`);
                return;
              }

              history.push(`/permanent-job/post?postingId=${row.id}`);
            }}
            showColumnsToHide={false}
            showTotal={false}
            conditionalRowStyles={[
              {
                when: (row) => row.status === 'draft',
                style: {
                  backgroundColor: '#D9F2F3',
                },
              },
              {
                when: (row) => row.status === 'active',
                style: {
                  backgroundColor: '#99DAE3',
                },
              },
              {
                when: (row) => row.status === 'suspended',
                style: {
                  backgroundColor: '#FFDB9A',
                },
              },
              {
                when: (row) => row.status === 'closed',
                style: {
                  backgroundColor: '#AAB7C4',
                },
              },
            ]}
          />
        </TabViewContainer>
      </TabViewContainer>
    </div>
  );
};
